import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { dailyVerseAPI } from '../utils/api';
import { speakArabic } from '../utils/tts';
import { Sparkles, Volume2, Share2, ArrowRight, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const DailyVerseCard = ({ greetingName }) => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [verse, setVerse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dailyVerseAPI.today()
      .then((r) => { if (!cancelled) setVerse(r.data); })
      .catch(() => { if (!cancelled) setVerse(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-3xl bg-white border border-stone-200 p-6 animate-pulse">
        {greetingName !== undefined && (
          <div className="mb-5 pb-5 border-b border-stone-200/60">
            <div className="h-3 w-20 bg-stone-200 rounded mb-2" />
            <div className="h-8 w-3/4 bg-stone-200 rounded" />
          </div>
        )}
        <div className="h-4 w-24 bg-stone-200 rounded mb-3" />
        <div className="h-10 bg-stone-100 rounded mb-2" />
        <div className="h-4 bg-stone-100 rounded w-3/4" />
      </div>
    );
  }
  if (!verse) {
    if (greetingName === undefined) return null;
    // Render at least the greeting so the user isn't shown a blank page if the
    // daily verse service is unavailable.
    return (
      <div
        className={`relative overflow-hidden rounded-3xl border-2 p-6 sm:p-8 ${isKidsMode ? 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50' : 'border-teal-200 bg-gradient-to-br from-teal-50 via-white to-teal-50'}`}
        data-testid="daily-verse-card-fallback"
      >
        <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1.5">
          {lang === 'tr' ? 'Hoş geldiniz' : 'Welcome'}
        </p>
        <h1
          className={`text-3xl sm:text-4xl font-bold tracking-tight ${isKidsMode ? 'text-amber-900' : 'text-teal-900'}`}
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="home-greeting"
        >
          {greetingName ? `Selâmün Aleyküm, ${greetingName}` : 'Selâmün Aleyküm'}
        </h1>
      </div>
    );
  }

  const meal = lang === 'en' ? verse.meal_en : verse.meal_tr;
  const surahLabel = `${verse.surah_name_en} ${verse.surah_number}:${verse.ayah_number_in_surah}`;

  const togglePlay = async () => {
    if (playing) return;
    setPlaying(true);
    await speakArabic(verse.arabic, {
      onEnd: () => setPlaying(false),
      onError: () => { setPlaying(false); toast.error('Ses oynatılamadı'); },
    });
  };

  const shareText = `${verse.arabic}\n\n"${meal}"\n— ${surahLabel}\n\nHafızım üzerinden günün ayeti · hafizim.com`;

  const shareWhatsapp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'noopener');
  };
  const shareX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText.slice(0, 260))}`;
    window.open(url, '_blank', 'noopener');
  };
  const copyToClipboard = () => {
    navigator.clipboard?.writeText(shareText).then(() => {
      setCopied(true);
      toast.success(lang === 'tr' ? 'Kopyalandı!' : 'Copied!');
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => toast.error('Kopyalanamadı'));
  };

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border-2 p-6 sm:p-8 ${isKidsMode ? 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50' : 'border-teal-200 bg-gradient-to-br from-teal-50 via-white to-teal-50'}`}
      data-testid="daily-verse-card"
      style={{ boxShadow: 'inset 0 0 0 1px rgba(217,170,80,0.08), 0 6px 20px rgba(120,80,20,0.05)' }}
    >
      {/* Decorative gradient blobs */}
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-20 blur-2xl" style={{ background: isKidsMode ? '#f59e0b' : '#14b8a6' }} />
      <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full opacity-20 blur-2xl" style={{ background: isKidsMode ? '#fde68a' : '#99f6e4' }} />

      <div className="relative">
        {/* Greeting (shown when greetingName is provided, e.g. on Home page) */}
        {greetingName !== undefined && (
          <div className="mb-5 pb-5 border-b border-stone-200/60">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1.5">
              {lang === 'tr' ? 'Hoş geldiniz' : 'Welcome'}
            </p>
            <h1
              className={`text-3xl sm:text-4xl font-bold tracking-tight ${isKidsMode ? 'text-amber-900' : 'text-teal-900'}`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="home-greeting"
            >
              {greetingName ? `Selâmün Aleyküm, ${greetingName}` : 'Selâmün Aleyküm'}
            </h1>
          </div>
        )}

        <div className="flex items-center gap-2 mb-3">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${isKidsMode ? 'bg-amber-500 text-white' : 'bg-teal-700 text-white'}`}>
            <Sparkles className="w-3.5 h-3.5" />
            {lang === 'tr' ? 'Günün Ayeti' : 'Verse of the Day'}
          </div>
          <span className="text-xs text-stone-500 font-mono">{verse.date}</span>
        </div>

        <p
          className="text-stone-900 mb-5 text-right"
          style={{
            fontFamily: '"Amiri Quran", Amiri, "Noto Naskh Arabic", serif',
            direction: 'rtl',
            fontSize: 'clamp(22px, 3vw, 32px)',
            lineHeight: 1.9,
          }}
          data-testid="daily-verse-arabic"
        >
          {verse.arabic}
        </p>

        <p className="text-stone-700 text-base sm:text-lg leading-relaxed mb-4" data-testid="daily-verse-meal">
          &ldquo;{meal}&rdquo;
        </p>

        <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-stone-200/60">
          <div className="flex items-center gap-1.5 text-sm text-stone-600">
            <span className="font-bold text-stone-800" style={{ fontFamily: 'Outfit, sans-serif' }}>{verse.surah_name_en}</span>
            <span>·</span>
            <span>{verse.surah_number}:{verse.ayah_number_in_surah}</span>
            {verse.juz && <><span>·</span><span>{lang === 'tr' ? 'Cüz' : 'Juz'} {verse.juz}</span></>}
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={togglePlay}
              data-testid="daily-verse-tts"
              disabled={playing}
              title={lang === 'tr' ? 'Dinle' : 'Listen'}
              className={`w-9 h-9 rounded-full flex items-center justify-center ${playing ? (isKidsMode ? 'bg-amber-500 text-white' : 'bg-teal-700 text-white') : 'bg-white border border-stone-200 text-stone-600 hover:border-stone-400'}`}
            >
              <Volume2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={copyToClipboard}
              data-testid="daily-verse-copy"
              title={lang === 'tr' ? 'Kopyala' : 'Copy'}
              className="w-9 h-9 rounded-full bg-white border border-stone-200 text-stone-600 hover:border-stone-400 flex items-center justify-center"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={shareWhatsapp}
              data-testid="daily-verse-whatsapp"
              title="WhatsApp"
              className="w-9 h-9 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 flex items-center justify-center text-base"
            >
              <Share2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={shareX}
              data-testid="daily-verse-x"
              title="X"
              className="w-9 h-9 rounded-full bg-stone-900 text-white hover:bg-black flex items-center justify-center text-xs font-bold"
            >
              𝕏
            </button>
            <button
              type="button"
              onClick={() => navigate(`/oku/${verse.surah_number}`)}
              data-testid="daily-verse-readmore"
              className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-xs font-semibold ${isKidsMode ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-teal-700 text-white hover:bg-teal-800'}`}
            >
              {lang === 'tr' ? 'Devamını Oku' : 'Read Surah'} <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyVerseCard;
